import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Slide, Box, useTheme } from '@mui/material';
import { Close } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

// Transition for the dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * A customized modal/dialog component for the dashboard
 * 
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Function to call when closing the modal
 * @param {string} title - Modal title
 * @param {string} description - Modal description text
 * @param {node} actions - Action buttons to display at the bottom
 * @param {string} size - Modal size (xs, sm, md, lg, xl)
 * @param {boolean} fullWidth - Whether the modal should take full width
 * @param {boolean} fullScreen - Whether the modal should be full screen
 * @param {boolean} disableBackdropClick - Whether to disable closing on backdrop click
 * @param {boolean} disableEscapeKeyDown - Whether to disable closing on Escape key
 * @param {boolean} showCloseButton - Whether to show close button in header
 */
const DashboardModal = ({
  children,
  open,
  onClose,
  title,
  description,
  actions,
  size = 'md',
  fullWidth = true,
  fullScreen = false,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  showCloseButton = true,
}) => {
  const theme = useTheme();

  // Handle backdrop click
  const handleBackdropClick = (event) => {
    if (disableBackdropClick) {
      event.stopPropagation();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          TransitionComponent={Transition}
          keepMounted
          onClose={onClose}
          maxWidth={size}
          fullWidth={fullWidth}
          fullScreen={fullScreen}
          onBackdropClick={handleBackdropClick}
          disableEscapeKeyDown={disableEscapeKeyDown}
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: 'hidden',
            },
            component: motion.div,
            initial: { opacity: 0, y: 20 },
            animate: { opacity: 1, y: 0 },
            exit: { opacity: 0, y: 20 },
            transition: { duration: 0.3 },
          }}
        >
          {/* Modal Header */}
          {title && (
            <DialogTitle
              sx={{
                px: 3,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>{title}</Box>
              {showCloseButton && (
                <IconButton
                  edge="end"
                  color="inherit"
                  onClick={onClose}
                  aria-label="close"
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.action.hover,
                    '&:hover': {
                      backgroundColor: theme.palette.action.selected,
                    },
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
            </DialogTitle>
          )}

          {/* Modal Content */}
          <DialogContent sx={{ px: 3, py: 2 }}>
            {description && (
              <DialogContentText sx={{ mb: 3 }}>
                {description}
              </DialogContentText>
            )}
            {children}
          </DialogContent>

          {/* Modal Actions */}
          {actions && (
            <DialogActions sx={{ px: 3, py: 2 }}>
              {actions}
            </DialogActions>
          )}
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default DashboardModal;